import { BotProfile, Card, GameState, Player } from './types';
import { canCallBitIt } from './game';

export interface BotDecision {
  action: 'play_card' | 'call_bit_it' | 'respond_challenge';
  cardId?: string;
  challengeResponse?: 'face' | 'fold';
}

export function decideBotAction(state: GameState, botId: string): BotDecision {
  const bot = state.players.find((p) => p.id === botId);
  if (!bot || bot.folded || bot.hand.length === 0) {
    throw new Error(`Bot ${botId} is not in a valid state to take an action`);
  }

  const profile: BotProfile = bot.botProfile || 'minimax';

  // 1. Is there a pending challenge requiring this bot's response?
  if (state.challenge.status === 'pending' && state.challenge.responses[botId] === 'pending') {
    return decideChallengeResponse(state, bot, profile);
  }

  // 2. Can the bot call Bit-It on its turn before playing?
  if (state.currentTurnPlayerId === botId && canCallBitIt(state, botId)) {
    const shouldRaise = decideBitItCall(state, bot, profile);
    if (shouldRaise) {
      return { action: 'call_bit_it' };
    }
  }

  // 3. Otherwise, play a card
  if (state.currentTurnPlayerId === botId) {
    const cardId = decideCardToPlay(state, bot, profile);
    return { action: 'play_card', cardId };
  }

  throw new Error(`Bot ${botId} has no action available`);
}

function decideChallengeResponse(
  state: GameState,
  bot: Player,
  profile: BotProfile
): BotDecision {
  const handStrength = evaluateHandStrength(bot.hand, state);

  const canReRaise = canCallBitIt(state, bot.id);

  if (profile === 'aggressive') {
    // If holding a very strong hand, re-raise to Double Bit-It!
    if (canReRaise && handStrength >= 0.68) {
      return { action: 'call_bit_it' };
    }
    // Aggressive rarely folds (folds only with extremely weak hand, power < 2.0)
    if (handStrength < 0.25) {
      return { action: 'respond_challenge', challengeResponse: 'fold' };
    }
    return { action: 'respond_challenge', challengeResponse: 'face' };
  }

  if (profile === 'cautious') {
    // Cautious never re-raises; folds unless hand strength is above average
    if (handStrength < 0.55) {
      return { action: 'respond_challenge', challengeResponse: 'fold' };
    }
    return { action: 'respond_challenge', challengeResponse: 'face' };
  }

  // Minimax / Economic profile
  // Re-raises to Double Bit-It if EV is overwhelmingly positive
  if (canReRaise && handStrength >= 0.78) {
    return { action: 'call_bit_it' };
  }

  // Compares expected value of facing vs folding
  const stakesWin = state.challenge.level === 'bit_it' ? 4 : 8;
  const stakesFold = state.challenge.level === 'bit_it' ? 1 : 2;

  // Expected Value: EV = (winProb * stakesWin) - ((1 - winProb) * stakesFold)
  const expectedValue = (handStrength * stakesWin) - ((1 - handStrength) * stakesFold);
  if (expectedValue >= 0 || handStrength >= 0.38) {
    return { action: 'respond_challenge', challengeResponse: 'face' };
  }
  return { action: 'respond_challenge', challengeResponse: 'fold' };
}

function decideBitItCall(state: GameState, bot: Player, profile: BotProfile): boolean {
  // If only 1 trick left and losing, or already called
  const handStrength = evaluateHandStrength(bot.hand, state);

  if (profile === 'aggressive') {
    // Aggressive calls Bit-It with >= 40% hand strength or 30% random bluff
    if (handStrength >= 0.45 || Math.random() < 0.25) {
      return true;
    }
    return false;
  }

  if (profile === 'cautious') {
    // Cautious only calls if holding very strong hand (strength >= 0.75)
    return handStrength >= 0.75;
  }

  // Minimax: calls when win probability > 62%
  return handStrength >= 0.62;
}

function decideCardToPlay(state: GameState, bot: Player, profile: BotProfile): string {
  const hand = bot.hand;
  const currentPlays = state.currentTrick.plays;

  // RULE CHECK: Corven Slay Opportunity!
  // If Ezarion was played by an opponent in the current trick, and Bot has Corven:
  // Play Corven IMMEDIATELY to win the round!
  const hasCorven = hand.find((c) => c.isCorven);
  if (hasCorven) {
    const ezarionPlay = currentPlays.find((p) => p.card.isEzarion);
    if (ezarionPlay) {
      let isOpponent = true;
      if (state.mode === 'teams') {
        const ezarionPlayer = state.players.find((p) => p.id === ezarionPlay.playerId);
        isOpponent = ezarionPlayer?.team !== bot.team;
      } else {
        isOpponent = ezarionPlay.playerId !== bot.id;
      }
      if (isOpponent) {
        return hasCorven.id; // INSTANT CORVEN SLAY!
      }
    }
  }

  // If Bot is leading the trick (plays is empty)
  if (currentPlays.length === 0) {
    return decideLeadCard(state, bot, profile);
  }

  // Bot is responding in the trick:
  // Find current highest power on the table
  const maxTablePower = Math.max(...currentPlays.map((p) => p.card.power));

  // Cards that can beat current table
  // Filter out Corven unless Ezarion is on table (handled above)
  const winningCards = hand.filter((c) => c.power > maxTablePower);

  if (profile === 'aggressive') {
    // Aggressive likes to overpower with highest winning card
    if (winningCards.length > 0) {
      winningCards.sort((a, b) => b.power - a.power);
      return winningCards[0].id;
    }
    // Cannot win: dump lowest card
    const nonCorven = hand.filter((c) => !c.isCorven);
    const sorted = nonCorven.length > 0 ? nonCorven : hand;
    sorted.sort((a, b) => a.power - b.power);
    return sorted[0].id;
  }

  if (profile === 'cautious') {
    // Cautious wins as cheaply as possible
    if (winningCards.length > 0) {
      winningCards.sort((a, b) => a.power - b.power); // Lowest winning card
      return winningCards[0].id;
    }
    // Cannot win: discard lowest card, but preserve Corven if Ezarion not yet played
    const nonCorven = hand.filter((c) => !c.isCorven);
    const sorted = nonCorven.length > 0 ? nonCorven : hand;
    sorted.sort((a, b) => a.power - b.power);
    return sorted[0].id;
  }

  // Minimax / Economic:
  // If teammate in 2v2 is currently winning the trick with a strong card, dump a low card!
  if (state.mode === 'teams') {
    const winningPlay = currentPlays.find((p) => p.card.power === maxTablePower);
    if (winningPlay) {
      const winningPlayer = state.players.find((p) => p.id === winningPlay.playerId);
      if (winningPlayer?.team === bot.team && maxTablePower >= 7) {
        // Teammate has it secured with high gold card, play lowest
        const sorted = [...hand].sort((a, b) => a.power - b.power);
        return sorted[0].id;
      }
    }
  }

  // If we can win, use the lowest winning card (economy of power)
  if (winningCards.length > 0) {
    winningCards.sort((a, b) => a.power - b.power);
    return winningCards[0].id;
  }

  // Cannot win trick: dump lowest card, save Corven if Ezarion is still unseen
  const ezarionSeen = isEzarionAccountedFor(state);
  const discardable = ezarionSeen ? hand : hand.filter((c) => !c.isCorven);
  const pool = discardable.length > 0 ? discardable : hand;
  pool.sort((a, b) => a.power - b.power);
  return pool[0].id;
}

function decideLeadCard(state: GameState, bot: Player, profile: BotProfile): string {
  const hand = bot.hand;

  if (state.currentTrick.trickNumber === 1) {
    // "Fazer a primeira manda no jogo": Trick 1 is critical!
    if (profile === 'aggressive' || profile === 'minimax') {
      // Lead with strong card (powers 6 to 10), but avoid leading Corven (power 0) or Ezarion if Corvens are unknown
      const candidates = hand.filter((c) => !c.isCorven && !c.isEzarion);
      if (candidates.length > 0) {
        candidates.sort((a, b) => b.power - a.power);
        return candidates[0].id;
      }
    }
  }

  // If bot already won Trick 1, on Trick 2 lead lowest to conserve
  if (state.completedTricks[0]?.winnerId === bot.id) {
    const candidates = hand.filter((c) => !c.isCorven);
    if (candidates.length > 0) {
      candidates.sort((a, b) => a.power - b.power);
      return candidates[0].id;
    }
  }

  // General lead: highest non-corven card
  const nonCorven = hand.filter((c) => !c.isCorven);
  const pool = nonCorven.length > 0 ? nonCorven : hand;
  pool.sort((a, b) => b.power - a.power);
  return pool[0].id;
}

export function evaluateHandStrength(hand: Card[], state: GameState): number {
  if (hand.length === 0) return 0;

  let totalScore = 0;
  for (const card of hand) {
    if (card.isEzarion) {
      // Ezarion is worth 1.0, slightly discounted if Corvens are still unseen
      const corvensSeen = countCorvensSeen(state);
      totalScore += corvensSeen >= 2 ? 0.95 : 0.85;
    } else if (card.isCorven) {
      // Corven is worth more if Ezarion is still unseen
      const ezarionSeen = isEzarionAccountedFor(state);
      totalScore += ezarionSeen ? 0.05 : 0.45;
    } else {
      // Normalized power: 1 = 0.1, 5 = 0.5, 6 = 0.6, 9 = 0.9
      totalScore += card.power / 10;
    }
  }

  return totalScore / hand.length;
}

function countCorvensSeen(state: GameState): number {
  let count = 0;
  for (const trick of state.completedTricks) {
    for (const p of trick.plays) {
      if (p.card.isCorven) count++;
    }
  }
  for (const p of state.currentTrick.plays) {
    if (p.card.isCorven) count++;
  }
  return count;
}

function isEzarionAccountedFor(state: GameState): boolean {
  for (const trick of state.completedTricks) {
    for (const p of trick.plays) {
      if (p.card.isEzarion) return true;
    }
  }
  for (const p of state.currentTrick.plays) {
    if (p.card.isEzarion) return true;
  }
  return false;
}
